import { useCallback, useEffect, useRef, RefObject } from "react"
import Game from "../Model/Game"

const DEFER_MS = 300

type ReturnType = {
  start: () => void
  stop: () => void
}

export default function useGameCountdown(
  game: Game | null,
  setGame: (g: Game) => void,
): ReturnType {
  const runningRef: RefObject<boolean> = useRef(false)
  const rafRef: RefObject<number | null> = useRef(null)
  const lastFrameRef: RefObject<number | null> = useRef(null)
  const deferredRef: RefObject<number> = useRef(0)

  const gameRef: RefObject<Game | null> = useRef(game)
  const timeRef: RefObject<number> = useRef(game?.time ?? 0)

  // Reset internal refs whenever a new game is passed in
  useEffect((): void => {
    gameRef.current = game
    if (game !== null) {
      timeRef.current = game.time
      deferredRef.current = 0
      lastFrameRef.current = null
    }
  }, [game])

  const loop = useCallback((): void => {
    const currentGame: Game | null = gameRef.current
    if (!runningRef.current || currentGame === null) {
      return
    }

    const now: number = performance.now()
    const delta: number =
      lastFrameRef.current === null ? 0 : now - lastFrameRef.current
    lastFrameRef.current = now

    // Countdown
    let newTime: number = Math.max(0, timeRef.current - delta)
    timeRef.current = newTime

    if (newTime <= 0) {
      runningRef.current = false
      setGame(currentGame.defeat("Time's up!").setTime(0))

      return
    }

    // Throttle updates to React
    deferredRef.current += delta
    if (deferredRef.current >= DEFER_MS) {
      setGame(currentGame.setTime(newTime))
      deferredRef.current = 0
    }

    rafRef.current = requestAnimationFrame(loop)
  }, [setGame])

  const start = useCallback((): void => {
    if (gameRef.current === undefined || runningRef.current) {
      return
    }
    runningRef.current = true
    lastFrameRef.current = null
    rafRef.current = requestAnimationFrame(loop)
  }, [loop])

  const stop = useCallback((): void => {
    runningRef.current = false
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    lastFrameRef.current = null

    const currentGame: Game | null = gameRef.current
    if (currentGame !== null) {
      setGame(currentGame.setTime(timeRef.current))
    }
  }, [setGame])

  // Cleanup on unmount
  useEffect(() => {
    return (): void => stop()
  }, [stop])

  return { start, stop }
}
